<template>
  <!-- 合规文档上传组件容器 -->
  <div class="activityFile common_group">
    <div class="wrapper">
      <!-- 文件上传组件 -->
      <el-upload
        class="upload"
        :action="baseUrl"
        :before-upload="beforeUpload"
        :on-success="handleSuccess"
        :on-progress="uploadVideoProcess"
        :on-error="handleError"
        multiple
        :disabled="btnDisabled"
        :show-file-list="false"
      >
        <div>
          <!-- 上传按钮 -->
          <el-button type="primary" :disabled="btnDisabled">
            <i class="iconfont icon-shangchuan-icon1" /> <!-- 上传图标 -->
            {{ $t('file.ctu') }} <!-- 上传按钮文本，通过i18n国际化 -->
          </el-button>
          <!-- 文件大小提示 -->
          <span slot="tip">{{ $t('file.F50M') }}</span>
        </div>
      </el-upload>

      <!-- 自定义文件列表展示 -->
      <ul class="fileList">
        <li v-for="(item, index) in fileList" :key="item.id || index">
          <!-- 文件图标 - 添加空值检查 -->
          <img :src="imgUrl(item.name || item.fileName || '')" alt="文件图标">
          <!-- 文件名 -->
          <span>{{ item.name || item.fileName || '未知文件' }}</span>
          <!-- 删除按钮（非上传中状态显示） -->
          <i
            class="iconfont icon--s-guanbi closeIconFont"
            v-show="!progressFlag"
            @click="beforeRemove(index)"
          />
          <!-- 上传进度条（只在最后一个文件且上传中显示） -->
          <el-progress
            v-if="index === fileList.length - 1 && progressFlag"
            :percentage="loadProgress"
          />
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
// 导入API服务模块
import { newApplicationFormApi } from '@/views/newApplicationForm/service/index.js';
import { complianceMixin } from './complianceMixin';

// 导出Vue组件
export default {
  mixins: [complianceMixin],
  props: {
    editEchoData: {
      type: Object,
      default: () => {}
    }
  },
  // 组件数据定义
  data () {
    return {
      loadProgress: 0, // 上传进度百分比
      progressFlag: false, // 进度条显示标志
      btnDisabled: false, // 按钮禁用状态
      fileList: [], // 文件列表
      // 从环境变量获取基础URL，提供回退值
      // baseUrl: process.env.VUE_APP_FILESERVICE_URL,
      baseUrl: 'https://fileserviceoutside.ruijie.com.cn/api/file/marketActivity/15008559650934784/15008559650934784'
    };
  },
  watch: {
    // 监听listedFiles变化，同步到fileList
    editEchoData: {
      handler (newVal) {
        if (newVal.complianceProcessDetailVO && Array.isArray(newVal.complianceProcessDetailVO.complianceAttachFileList)) {
          this.fileList = newVal.complianceProcessDetailVO.complianceAttachFileList.map(item => {
            item.name = item.fileName;
            return item;
          });
        }
      },
      deep: true,
      immediate: true
    }
  },
  // 组件方法定义
  methods: {
    /**
     * 验证处理函数 - 检查文件列表中所有文件是否上传成功
     * @returns {boolean} 验证结果，所有文件上传成功返回true
     */
    validateHandler () {
      // 空列表视为验证通过
      if (!this.fileList || this.fileList.length === 0) {
        return true;
      }

      let result = true;
      let messageInfo = '';

      // 遍历文件列表，检查每个文件是否有有效的FID或fileId
      result = this.fileList.every(item => {
        const isValid = item && ((item.response && item.response.data?.[0]?.FID) || item.fileId);
        if (!isValid && item) {
          messageInfo = item.name ? `${item.name} ${this.$t('file.FPdar')}` : this.$t('file.FPdaua');
        }
        return isValid;
      });

      // 验证失败时显示错误信息
      if (!result) {
        this.$message.error(messageInfo);
      }

      return result;
    },

    /**
     * 文件上传前处理函数
     * @param {File} file - 待上传的文件对象
     * @returns {Promise} 上传前处理Promise
     */
    beforeUpload (file) {
      // 设置按钮禁用状态
      this.btnDisabled = true;

      return new Promise((resolve, reject) => {
        // 参数验证
        if (!file) {
          this.$message.error('文件无效');
          reject(new Error('文件无效'));
          this.btnDisabled = false;
          return;
        }

        // 检查文件大小是否小于100MB
        const fileSize = file.size / 1024 / 1024 < 50;
        if (!fileSize) {
          const errorMsg = this.$t('file.uFn50M');
          this.$message.error(errorMsg);
          reject(new Error(errorMsg));
          this.btnDisabled = false;
          return;
        }

        // 获取上传令牌
        newApplicationFormApi.getFileToken()
          .then(res => {
            if (res && res.status === '200' && res.data) {
              // 如果baseUrl中没有Token参数，则添加Token
              if (this.baseUrl && this.baseUrl.indexOf('?Token=') < 0) {
                this.baseUrl = `${this.baseUrl}?Token=${res.data}`;
              }

              // 重置上传进度
              this.loadProgress = 0;

              // 将文件添加到文件列表，添加唯一ID
              this.fileList.push({
                id: Date.now() + Math.random().toString(36).substr(2, 9), // 生成唯一ID
                name: file.name
              });

              // 允许上传
              resolve();
            } else {
              // 获取Token失败，拒绝上传
              this.$message.error('获取上传权限失败');
              reject(new Error('获取Token失败'));
              this.btnDisabled = false;
            }
          })
          .catch(error => {
            console.error('获取文件Token失败:', error);
            this.$message.error('获取上传权限失败，请稍后重试');
            reject(error);
            this.btnDisabled = false;
          });
      });
    },

    /**
     * 上传进度处理函数 - 模拟上传进度动画效果
     * @param {Object} event - 上传进度事件对象
     * @param {File} file - 当前上传的文件
     * @param {Array} fileList - 文件列表
     */
    uploadVideoProcess (event, file, fileList) {
      // 设置进度条显示标志
      this.progressFlag = true;

      // 当上传进度达到100%时（实际传输完成）
      if (event && event.percent >= 100) {
        // 创建定时器，模拟处理中动画效果
        let interval = setInterval(() => {
          // 每次增加20%的加载进度
          this.loadProgress += 20;
          // 当进度达到99%时停止（等待实际响应）
          if (this.loadProgress >= 99) {
            this.loadProgress = 99;
            clearInterval(interval);
          }
        }, 100); // 每100毫秒更新一次
      }
    },

    /**
     * 上传成功处理函数
     * @param {Object} response - 上传响应数据
     * @param {File} file - 上传的文件
     * @param {Array} fileList - 文件列表
     */
    handleSuccess (response, file, fileList) {
      try {
        // 检查响应状态和FID
        if (response && response.status === '200' && response.data?.[0]?.FID) {
          // 设置进度为100%
          this.loadProgress = 100;

          // 查找对应文件在列表中的索引
          let fileIndex = this.fileList.findIndex(item =>
            item.name === (response.data?.[0]?.FullName || file?.name)
          );

          // 如果找到对应的文件，更新fileId
          if (fileIndex !== -1) {
            this.fileList[fileIndex].fileId = response.data[0].FID;
          }

          // 延迟隐藏进度条
          setTimeout(() => {
            this.progressFlag = false;
          }, 800);
        } else {
          // 上传失败，移除最后一个文件
          if (this.fileList.length > 0) {
            this.fileList.splice(this.fileList.length - 1, 1);
          }
          this.progressFlag = false;
          // 显示错误信息
          this.$message.error(response?.err || '文件上传失败');
        }
      } catch (error) {
        console.error('处理上传成功响应时出错:', error);
        this.$message.error('文件处理失败');
        this.progressFlag = false;
      } finally {
        // 恢复按钮状态
        this.btnDisabled = false;
        // 安全重置baseUrl，移除Token参数
        if (this.baseUrl) {
          this.baseUrl = this.baseUrl.split('?Token')[0];
        }
      }
    },

    /**
     * 上传错误处理函数
     * @param {Error} error - 错误对象
     * @param {File} file - 上传的文件
     * @param {Array} fileList - 文件列表
     */
    handleError (error, file, fileList) {
      console.error('文件上传错误:', error);
      this.$message.error('文件上传失败，请稍后重试');
      this.btnDisabled = false;
      this.progressFlag = false;

      // 移除失败的文件
      if (this.fileList.length > 0) {
        this.fileList.splice(this.fileList.length - 1, 1);
      }
    },

    /**
     * 文件移除前处理函数
     * @param {number} index - 要移除的文件索引
     */
    beforeRemove (index) {
      // 参数验证
      if (index === undefined || index === null || index < 0 || index >= this.fileList.length) {
        console.error('无效的文件索引');
        return;
      }

      // 从文件列表中移除指定索引的文件
      this.fileList.splice(index, 1);
      // 可以在这里添加删除成功提示或其他逻辑
    }
  }
};
</script>

<style lang="scss" scoped>
// 导入通用样式
@import '@/style/common.scss';

// 活动文件组件样式
.activityFile {
  >.wrapper {
    background: #FCFDFE;        // 背景色
    border: 1px solid #DEE1E7;  // 边框
    line-height: 30px;          // 行高
    position: relative;         // 相对定位
    @extend .clear;             // 扩展clear样式（清除浮动）

    // 顶部装饰线
    &:before {
      content: '';
      background: #FFFFFF;
      width: 100%;
      height: 1px;
      position: absolute;
      top: 0;
      left: 0;
    }

    // 文件列表样式
    >ul {
      >li {
        padding: 15px 0 15px 20px;      // 内边距
        border-top: 1px solid #E2E4E9;  // 上边框
        height: 20px;                   // 高度
        line-height: 20px;              // 行高
        box-sizing: content-box;        // 盒模型
        >img { /* 文件图标样式 */
          vertical-align: middle;
        }
        // 文件图标样式
        >i:first-child {
          font-size: 18px;         // 字体大小
          margin-right: 15px;      // 右侧边距
          color: #B8B8B8;          // 颜色
        }

        // 文件名样式
        >span {
          margin-right: 40px;      // 右侧边距
          margin-left: 10px;
        }

        // 删除图标样式
        >.closeIconFont {
          display: none;           // 默认隐藏
          vertical-align: middle;  // 垂直居中
          font-size: 16px;         // 字体大小
          cursor: pointer;         // 鼠标指针
          color: #dc5650;          // 颜色（红色）
        }

        // 鼠标悬停时显示删除图标
        &:hover>.closeIconFont {
          display: inline-block;
        }

        // 进度条样式
        >.el-progress {
          width: 300px;            // 宽度
          display: inline-block;   // 行内块显示
        }
      }
    }

    // 上传组件样式（深度选择器）
    ::v-deep>.upload {
      >.el-upload {
        padding: 20px;             // 内边距

        >div>span {
          margin-left: 10px;       // 左侧边距
          color: #5B646E;          // 颜色
        }
      }
    }
  }
}
</style>
